import { useStyles } from "./Paywall.style";
import {
  FC,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { Typography } from "../../components/core/Typography/Typography";
import BackgroundCard1x from "../../assets/subscription/backgroundCard1x.webp";
import BackgroundCard2x from "../../assets/subscription/backgroundCard2x.webp";
import BackgroundCard3x from "../../assets/subscription/backgroundCard3x.webp";

import { StaticImage } from "../../components/core/StaticImage/StaticImage";
import { TypographyColor, TypographyVariant } from "../../constants/typography";
import Button from "../../components/core/Button/Button";
import clsx from "classnames";
import { useQuery } from "@apollo/client";
import { Query, SubscriptionPlan, User } from "@onmo/graphql/graphql";
import { getSubscriptionPlansGQL, meAAAGQL } from "../../graphql";
import { setSkippedActivationCode } from "../../models/storage";
import { AnalyticsGA4 } from "../../models/analyticsGA4";
import ActivateQrPopup from "../../components/common/ActivateQrPopup/ActivateQrPopup";
import ConfirmSubscriptionPopup from "../../components/common/ConfirmSubscriptionPopup/ConfirmSubscriptionPopup";
import BillingInfoPopup from "../../components/common/BillingInfoPopup/BillingInfoPopup";
import { isTV } from "../../utils/platform";
import { PageRoutes } from "../../constants/routes";
import { Icon } from "../../components/core/Icon/Icon";
import {
  useActiveElement,
  useGamepadContext,
} from "../../contexts/GamepadProvider";
import { useAppSelector } from "../../components/hooks";
import { selectIsDesktopView } from "../../store/slices/appState";
import { useSubscriptionFlow } from "../../hooks/useSubscriptionFlow";
import { LoadingScreen } from "../../components/common/LoadingScreen/LoadingScreen";
import { useBackNavigation } from "../../hooks/useBackNavigation";
import AccountMismatchPopup from "../../components/common/AccountMismatchPopup/AccountMismatchPopup";
import { useTranslation } from "react-i18next";
import { getCurrencySymbol } from "../../models/user";
import ShopPopup from "../../components/common/ShopPopup/ShopPopup";
import {
  FLIPKART_URL,
  PAYWALL_HEADER,
  PaywallVariant,
  separateThousands,
} from "../../constants/paywall";
import { getAppLanguage } from "../../models/language";
import { onRowMoveAction } from "../../models/gamepad";
import { animatedScrollIntoView } from "../../utils/smoothScroll";

type PlanFrequency = "MONTHLY" | "YEARLY";

const DEFAULT_RENEWAL_FREQUENCY: PlanFrequency = "MONTHLY";

const Paywall: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const goBack = useBackNavigation();
  const bottomBarRef = useRef<HTMLDivElement>(null);
  const [activateQRLink, setActivateQRLink] = useState("");
  const [isMismatchPopupOpen, setIsMismatchPopupOpen] = useState(false);
  // const [isShopPopupOpen, setIsShopPopupOpen] = useState(false);
  const [renewalFrequency, setRenewalFrequency] = useState<PlanFrequency>(
    DEFAULT_RENEWAL_FREQUENCY,
  );
  const { fromTo } = (location.state ?? {}) as {
    fromTo: "onboarding" | "console" | "game";
  };
  const { data: dataSubscriptionPlans, loading: loadingPlans } =
    useQuery<Query>(getSubscriptionPlansGQL, {
      variables: { input: { productId: "aaa" } },
    });

  const { data: meData, loading: loadingMe } = useQuery<{ me: User }>(meAAAGQL);

  const isLoading = loadingPlans || loadingMe;

  const isFounder = !!meData?.me?.metadata?.tags?.includes("op_founders");

  const isOpSpain = meData?.me?.metadata?.tags?.includes("op_spain");

  const tvUserId = useMemo(
    () => new URLSearchParams(location.search).get("tvUserId"),
    [location.search],
  );

  const isDesktop = useAppSelector(selectIsDesktopView);
  const { setCallback, cleanup } = useGamepadContext();
  const activeElement = useActiveElement();
  const plans = dataSubscriptionPlans?.subscriptionPlans?.items ?? [];

  const hasNoPlans =
    plans.length === 0 || !plans.some((plan) => plan?.metadata?.shouldDisplay);

  const bundlePlan = plans.find(
    (plan: SubscriptionPlan) =>
      plan?.metadata?.type === "with_controller" &&
      plan?.metadata?.shouldDisplay,
  );

  const consolePlans = plans.filter(
    (plan) =>
      plan?.metadata?.type === "without_controller" &&
      plan?.metadata?.shouldDisplay,
  );

  const monthlyPlan = consolePlans.find(
    (plan) => plan?.metadata?.frequency === "MONTHLY",
  );

  const yearlyPlan = consolePlans.find(
    (plan) => plan?.metadata?.frequency === "YEARLY",
  );

  const isSingleConsolePlan = consolePlans.length === 1;

  const showPlanToggle = (!!monthlyPlan && !!yearlyPlan) || isOpSpain;

  const renewalPlan = renewalFrequency === "YEARLY" ? yearlyPlan : monthlyPlan;

  const getVariant = (): PaywallVariant => {
    if (isFounder) return "founder";
    if (showPlanToggle) return "renewal";
    if (isSingleConsolePlan) return "founder";
    return "newUser";
  };

  const variant = getVariant();

  const paywallHeader = PAYWALL_HEADER[variant];

  const lang = getAppLanguage();

  useEffect(() => {
    if (!tvUserId || isTV || !meData?.me?.id) return;
    if (tvUserId !== meData.me.id) {
      setIsMismatchPopupOpen(true);
    }
  }, [tvUserId, meData?.me?.id]);

  const {
    start,
    close: closeSubscriptionFlow,
    confirm: confirmSubscription,
    submitBilling,
    skipBilling,
    billingRegion,
    plan: selectedPlan,
    isConfirmOpen,
    isBillingOpen,
  } = useSubscriptionFlow();

  const handleClose = useCallback(() => {
    AnalyticsGA4.paywallSelect("skip");
    setSkippedActivationCode();
    goBack();
  }, [goBack]);

  const subscribeProController = () => {
    AnalyticsGA4.paywallSelect("bundle");
    if (isTV) {
      setActivateQRLink(FLIPKART_URL);
    } else {
      window.open(
        bundlePlan?.metadata?.purchaseUrl ?? FLIPKART_URL,
        "_blank",
        "noopener,noreferrer",
      );
    }
  };

  const subscribeConsolePlan = (plan?: SubscriptionPlan) => {
    if (!plan) return;
    AnalyticsGA4.paywallSelect("subscribe");
    if (isTV) {
      const urlPaywall = new URL(
        `${import.meta.env.VITE_APP_URL}${PageRoutes.Paywall}`,
      );
      if (meData?.me?.id) urlPaywall.searchParams.set("tvUserId", meData.me.id);

      setActivateQRLink(urlPaywall.toString());
      return;
    }
    start(plan);
  };

  useEffect(() => {
    setCallback({ onBackAction: handleClose, onMoveAction: onRowMoveAction });
    return () => {
      cleanup();
    };
  }, [setCallback, cleanup, handleClose]);

  useEffect(() => {
    AnalyticsGA4.paywallView(fromTo, "no_subscription");
  }, [fromTo]);

  useEffect(() => {
    const frequency = activeElement?.getAttribute("data-plan-frequency");
    if (frequency) setRenewalFrequency(frequency as PlanFrequency);
  }, [activeElement]);

  useEffect(() => {
    const card = activeElement?.closest<HTMLElement>("[data-paywall-card]");
    if (!card) return;
    animatedScrollIntoView(card, {
      axis: "y",
      block: "end",
      offsetBottom: (bottomBarRef.current?.offsetHeight ?? 0) + 50,
    });
  }, [activeElement]);

  const renderPlanCard = ({
    plan,
    price,
    onBuy,
    imageCardClassName,
  }: {
    plan?: SubscriptionPlan;
    price: ReactNode;
    onBuy: () => void;
    imageCardClassName?: string;
  }) => {
    const labels = plan?.labels?.[lang];
    const assets = plan?.assets?.[lang];
    const hideButton =
      plan?.metadata?.type === "with_controller" &&
      plan?.metadata?.action === "redirect" &&
      (plan?.metadata?.purchaseUrl?.length === 0 ||
        !plan?.metadata?.purchaseUrl);
    return (
      <div className={classes.bundleConsoleSection} data-paywall-card>
        <div className={classes.backgroundCardWrapper}>
          <StaticImage
            alt=""
            src={BackgroundCard1x}
            src2x={BackgroundCard2x}
            src3x={BackgroundCard3x}
            className={classes.backgroundCard}
          />
        </div>

        <div className={classes.inforCard}>
          <Typography
            variant={TypographyVariant.HeadingH2Bold}
            className={classes.bundleTitle}
          >
            {labels?.heading}
          </Typography>
          <div className={classes.featuresList}>
            {labels?.features?.map((feature: string, index: number) => (
              <div key={index} className={classes.featureItem}>
                <Icon
                  name="icon-bold-tick-circle"
                  className={classes.featureIcon}
                />
                <Typography
                  variant={TypographyVariant.BodyB4Regular}
                  className={classes.featureText}
                >
                  {feature}
                </Typography>
              </div>
            ))}
          </div>

          {price}

          {!hideButton && (
            <Button
              size="small"
              onClick={onBuy}
              className={clsx(
                classes.buyBtn,
                lang === "es" && classes.buyBtnEs,
              )}
              data-gamepad-no-auto-scroll="true"
            >
              {labels?.buttonText}
            </Button>
          )}
          <StaticImage
            alt={labels?.heading ?? ""}
            src={assets?.image1X}
            src2x={assets?.image2X}
            src3x={assets?.image3X}
            className={classes.productImg}
          />
        </div>
      </div>
    );
  };

  const formatPrice = (plan?: SubscriptionPlan, amount?: number) =>
    `${getCurrencySymbol(plan?.metadata?.currency)}${
      separateThousands(amount ?? 0) ?? ""
    }`;

  const renderBundleCard = () =>
    renderPlanCard({
      plan: bundlePlan,
      onBuy: subscribeProController,
      imageCardClassName: classes.bundleImgZoom,
      price: (
        <div className={classes.startingAt}>
          <Typography
            variant={TypographyVariant.BodyB2Regular}
            color={TypographyColor.Silver}
            className={classes.startingAtLabel}
          >
            {t("Paywall_Starting at")}
          </Typography>
          <Typography
            variant={TypographyVariant.HeadingH2Bold}
            className={classes.bundlePrice}
          >
            {formatPrice(bundlePlan, bundlePlan?.metadata?.netAmount)}
          </Typography>
        </div>
      ),
    });

  const renderFounderCard = () =>
    renderPlanCard({
      plan: monthlyPlan,
      onBuy: () => subscribeConsolePlan(monthlyPlan),
      price: (
        <div className={classes.founderPrice}>
          {monthlyPlan?.metadata?.originalNetAmount !==
            monthlyPlan?.metadata?.netAmount && (
            <Typography
              variant={TypographyVariant.BodyB2Regular}
              className={clsx(classes.strikeThrough, classes.oldPrice)}
            >
              {formatPrice(
                monthlyPlan,
                monthlyPlan?.metadata?.originalNetAmount || 0,
              )}
            </Typography>
          )}
          <div className={classes.founderPriceWrapper}>
            <Typography
              variant={TypographyVariant.HeadingH2Bold}
              className={classes.founderPriceText}
            >
              {formatPrice(monthlyPlan, monthlyPlan?.metadata?.netAmount)}
            </Typography>
            <Typography
              variant={TypographyVariant.BodyB4Medium}
              className={classes.perMonth}
            >
              {t("Paywall_/month")}
            </Typography>
          </div>
        </div>
      ),
    });

  const planToggleOptions: {
    frequency: PlanFrequency;
    label: string;
    plan?: SubscriptionPlan;
  }[] = [
    {
      frequency: "MONTHLY",
      label: t("Paywall_Monthly Plan"),
      plan: monthlyPlan,
    },
    { frequency: "YEARLY", label: t("Paywall_Yearly Plan"), plan: yearlyPlan },
  ];

  const renderRenewalCard = () => (
    <div className={classes.renewalSection}>
      <div className={classes.planToggle} role="radiogroup">
        {planToggleOptions.map(({ frequency, label, plan }) => {
          const isSelected = renewalFrequency === frequency;
          const tag = plan?.labels?.[lang]?.tag;
          return (
            <button
              key={frequency}
              type="button"
              role="radio"
              aria-checked={isSelected}
              tabIndex={-1}
              data-gamepad={`paywall-plan-${frequency.toLowerCase()}`}
              data-plan-frequency={frequency}
              data-selected-class={classes.planToggleFocused}
              className={classes.planToggleOption}
              onClick={() => setRenewalFrequency(frequency)}
            >
              {tag && (
                <Typography
                  variant={TypographyVariant.BodyB6Medium}
                  className={classes.planToggleTag}
                >
                  {tag}
                </Typography>
              )}
              <Typography
                variant={TypographyVariant.BodyB4Medium}
                className={classes.planToggleLabel}
              >
                {label}
              </Typography>
            </button>
          );
        })}
      </div>

      {renderPlanCard({
        plan: renewalPlan,
        onBuy: () => subscribeConsolePlan(renewalPlan),
        price: (
          <div className={classes.renewalPrice}>
            <Typography
              variant={TypographyVariant.HeadingH2Bold}
              className={classes.bundlePrice}
            >
              {formatPrice(renewalPlan, renewalPlan?.metadata?.netAmount)}
            </Typography>
            <Typography
              variant={TypographyVariant.BodyB4Medium}
              className={classes.renewalPriceUnit}
            >
              {renewalFrequency === "YEARLY"
                ? t("Paywall_/year")
                : t("Paywall_/month")}
            </Typography>
          </div>
        ),
      })}
    </div>
  );

  const renderCard = () => {
    if (hasNoPlans) return null;
    if ((isFounder || isOpSpain) && isSingleConsolePlan)
      return renderFounderCard();
    if (showPlanToggle) return renderRenewalCard();
    return renderBundleCard();
  };

  if (isLoading) return <LoadingScreen />;

  return (
    <div className={classes.root}>
      <div className={classes.background} />
      <div
        className={classes.header}
        role="button"
        tabIndex={-1}
        aria-label={t("A11y_Back")}
        onClick={handleClose}
      >
        <Icon
          name={"icon-linear-arrow-left-3"}
          className={classes.iconHeader}
          size={isDesktop ? 52 : 24}
          data-button-sound="back"
        />
      </div>
      <div className={classes.headerText}>
        <Typography
          variant={TypographyVariant.HeadingH1Bold}
          className={classes.mainText}
        >
          {t(paywallHeader.title)}
        </Typography>
        <Typography
          variant={TypographyVariant.BodyB1Regular}
          className={classes.subText}
        >
          {t(paywallHeader.subtitle)}
        </Typography>
      </div>
      <ActivateQrPopup
        onClose={() => setActivateQRLink("")}
        activateLink={activateQRLink}
      />
      <ConfirmSubscriptionPopup
        open={isConfirmOpen}
        price={selectedPlan?.metadata?.netAmount}
        onConfirm={confirmSubscription}
        onClose={closeSubscriptionFlow}
        gst={selectedPlan?.metadata?.taxRate}
        totalAmount={selectedPlan?.planAmount}
        currency={getCurrencySymbol(selectedPlan?.metadata?.currency)}
        taxType={selectedPlan?.metadata?.taxType}
      />
      <BillingInfoPopup
        open={isBillingOpen}
        region={billingRegion}
        onContinue={submitBilling}
        onSkip={skipBilling}
        onClose={closeSubscriptionFlow}
      />
      {/* <ShopPopup
        isShopPopupOpen={isShopPopupOpen}
        setIsShopPopupOpen={setIsShopPopupOpen}
      /> */}
      <div className={classes.contentSection}>{renderCard()}</div>
      <div className={classes.bottomBar} ref={bottomBarRef}>
        <Typography
          variant={
            isDesktop
              ? TypographyVariant.BodyB3Medium
              : TypographyVariant.BodyB6Medium
          }
          color={TypographyColor.Silver}
        >
          {t(
            "Paywall_Already have a Pro Controller Bundle? Scan the QR code on your Smart Console access key.",
          )}
        </Typography>
      </div>
      <AccountMismatchPopup
        isOpen={isMismatchPopupOpen}
        onClose={() => setIsMismatchPopupOpen(false)}
      />
    </div>
  );
};

export default Paywall;
